import { inject, Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap, firstValueFrom } from 'rxjs';
import { ApiService } from '../../../common/services/api.service';
import { ApiResponse } from '../../../common/services/api.types';

export interface BackofficeUser {
    email: string;
    firstname: string;
    lastname: string;
    role: string;
}

const AUTH_ENDPOINTS = {
    login: 'api/v1/backoffice/auth/login',
    me: 'api/v1/backoffice/auth/me',
    logout: 'api/v1/backoffice/auth/logout',
};

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);

    // Estado reactivo del usuario
    private readonly _currentUser = signal<BackofficeUser | null>(null);
    private readonly _isLoading = signal<boolean>(true);

    // Signals públicos
    readonly currentUser = this._currentUser.asReadonly();
    readonly isLoading = this._isLoading.asReadonly();
    readonly isAuthenticated = computed(() => this._currentUser() !== null);

    /**
     * Obtiene las iniciales del nombre del usuario
     */
    readonly userInitials = computed(() => {
        const user = this._currentUser();
        if (!user) return '';
        const first = user.firstname?.charAt(0) || '';
        const last = user.lastname?.charAt(0) || '';
        return (first + last).toUpperCase();
    });

    /**
     * Obtiene el nombre completo del usuario
     */
    readonly userFullName = computed(() => {
        const user = this._currentUser();
        if (!user) return '';
        return `${user.firstname || ''} ${user.lastname || ''}`.trim();
    });

    /**
     * Inicia sesión con email y password
     */
    login(email: string, password: string): Observable<ApiResponse<BackofficeUser>> {
        return this.api.post<BackofficeUser>(AUTH_ENDPOINTS.login, { email, password }).pipe(
            tap((response) => {
                if (response.success && response.data) {
                    this._currentUser.set(response.data);
                }
            })
        );
    }

    /**
     * Cierra la sesión actual
     */
    logout(): Observable<ApiResponse<null>> {
        return this.api.post<null>(AUTH_ENDPOINTS.logout).pipe(
            tap(() => {
                this._currentUser.set(null);
                this.router.navigate(['/auth/login']);
            })
        );
    }

    /**
     * Obtiene el usuario actual desde el servidor
     * Usado para verificar si la sesión sigue activa
     */
    fetchCurrentUser(): Observable<ApiResponse<BackofficeUser>> {
        this._isLoading.set(true);
        return this.api.get<BackofficeUser>(AUTH_ENDPOINTS.me).pipe(
            tap((response) => {
                if (response.success && response.data) {
                    this._currentUser.set(response.data);
                } else {
                    this._currentUser.set(null);
                }
                this._isLoading.set(false);
            })
        );
    }

    /**
     * Inicializa el estado de autenticación
     * Retorna Promise para que APP_INITIALIZER espere
     */
    initialize(): Promise<void> {
        return firstValueFrom(this.fetchCurrentUser()).then(() => { });
    }

    /**
     * Limpia el estado del usuario (usado por el guard cuando hay error)
     */
    clearUser(): void {
        this._currentUser.set(null);
        this._isLoading.set(false);
    }
}
